"use client";

import { m as motion, useReducedMotion } from "framer-motion";
import Link from "next/link";
import { useState } from "react";
import {
  ArrowRight,
  PhoneCall,
  Linkedin,
  Code2,
  Palette,
  Lightbulb,
  Zap,
  MapPin,
  Leaf,
  HardHat,
  Workflow,
} from "lucide-react";
import { useLocale } from "next-intl";
import { Layout } from "@/components/layout/Layout";
import { InBreve } from "@/components/shared/InBreve";
import { Button } from "@/components/ui/button";
import { localizedRoutes } from "@/lib/routes/routes";
import { useHeaderDebugProbe } from "@/hooks/useHeaderDebugProbe";
import { trackEvent } from "@/utils/analytics";
import { FourthDimensionVisual } from "@/components/visuals/FourthDimensionVisual";
import {
  NodeGraphGlyph,
  RadarGlyph,
} from "@/components/visuals/ChiSonoGlyphs";

const values = [
  {
    icon: Palette,
    title: { it: "Creatività", en: "Creativity" },
    description: {
      it: "Design e gusto. La parte che l'AI non sostituisce.",
      en: "Design and taste. The part AI can't replace.",
    },
  },
  {
    icon: Zap,
    title: { it: "Velocità", en: "Speed" },
    description: {
      it: "Uso l'AI come leva. Più veloce, senza perdere qualità.",
      en: "I use AI as leverage. Faster, without losing quality.",
    },
  },
  {
    icon: Code2,
    title: { it: "Qualità che dura", en: "Quality that lasts" },
    description: {
      it: "Codice pulito che regge nel tempo. Niente scorciatoie.",
      en: "Clean code that holds up over time. No shortcuts.",
    },
  },
  {
    icon: Lightbulb,
    title: { it: "Contatto diretto", en: "Direct contact" },
    description: {
      it: "Parli sempre con me. Nessun passaggio di mano.",
      en: "You always talk to me. No handoffs.",
    },
  },
];

const buildingHighlights = [
  {
    visual: "graph" as const,
    title: {
      it: "Company brain + preventivatore",
      en: "Company brain + quote engine",
    },
    description: {
      it: "Il cervello digitale della mia attività. Un sistema dove tutto si collega, così non perdo nulla.",
      en: "The digital brain of my business. A system where everything connects, so nothing slips through.",
    },
  },
  {
    visual: "radar" as const,
    title: {
      it: "Farsi trovare su Google e sugli assistenti AI",
      en: "Getting found on Google and AI assistants",
    },
    description: {
      it: "Automatizzo la visibilità su Google e sugli assistenti AI. Per me e per i clienti.",
      en: "I automate visibility on Google and AI assistants. For me and for clients.",
    },
  },
];

const buildingIdeas = [
  {
    icon: MapPin,
    label: { it: "Lead scraping + geo", en: "Lead scraping + geo" },
    stage: 0.5,
  },
  {
    icon: Leaf,
    label: { it: "App mappa-rifiuti", en: "Litter-mapping app" },
    stage: 0.04,
  },
  {
    icon: HardHat,
    label: { it: "Sicurezza sul lavoro", en: "Workplace safety" },
    stage: 0.06,
  },
  {
    icon: Workflow,
    label: { it: "Automazione del lavoro", en: "Work automation" },
    stage: 0.42,
  },
];

export default function ChiSonoClient() {
  const [photoError, setPhotoError] = useState(false);
  const locale = useLocale();
  useHeaderDebugProbe("chi-sono");
  const isItalian = locale.startsWith("it");
  const routes = localizedRoutes[isItalian ? "it" : "en"];
  const bookingLink = `${routes.contacts}#calendly`;
  const reduce = useReducedMotion();
  const feature = values[0];
  const restValues = values.slice(1);

  return (
    <Layout>
      {/* Hero */}
      <section className="section-padding pt-6 md:pt-20 xl:min-h-[calc(100dvh-5.5rem)] xl:flex xl:flex-col xl:justify-center xl:py-0 relative overflow-hidden">
        <div className="absolute inset-0 pattern-dots" />
        <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl" />

        <div className="container-wide relative">
          <div className="grid xl:grid-cols-2 gap-10 md:gap-8 xl:gap-12 items-center xl:items-end">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-left"
            >
              <span className="font-mono text-label text-primary mb-4 block">
                {isItalian ? "Chi sono" : "About"}
              </span>
              <h1 className="font-display font-bold tracking-tight leading-[1.1] text-[clamp(1.7rem,6vw,2.3rem)] md:text-[2.6rem] mb-6">
                {isItalian ? (
                  <>
                    Aiuto PMI e startup a trasformare idee e processi in{" "}
                    <span className="text-shine">software</span>,{" "}
                    <span className="text-shine">automazioni AI</span> e
                    prodotti che fanno crescere il business.
                  </>
                ) : (
                  <>
                    I help SMEs and startups turn ideas and processes into{" "}
                    <span className="text-shine">software</span>,{" "}
                    <span className="text-shine">AI automation</span> and
                    products that grow the business.
                  </>
                )}
              </h1>
              <div className="text-body-lg text-muted-foreground mb-6 space-y-2">
                <p>
                  {isItalian ? (
                    <>
                      Sono{" "}
                      <strong className="text-foreground">
                        Dario Marco Bellini
                      </strong>
                      , founder di Dimensione 4.
                    </>
                  ) : (
                    <>
                      I&apos;m{" "}
                      <strong className="text-foreground">
                        Dario Marco Bellini
                      </strong>
                      , founder of Dimensione 4.
                    </>
                  )}
                </p>
                <p>
                  <strong className="text-foreground">
                    {isItalian
                      ? "Lavori direttamente con me, dalla strategia alla consegna."
                      : "You work directly with me, from strategy to delivery."}
                  </strong>
                </p>
              </div>
              <p className="text-body text-muted-foreground mb-8 max-w-2xl leading-relaxed">
                {isItalian ? (
                  <>
                    L&apos;AI accelera il mio lavoro, ma{" "}
                    <span className="font-medium text-foreground">
                      creatività, design, visione e decisioni
                    </span>{" "}
                    restano umane.
                  </>
                ) : (
                  <>
                    AI speeds up my work, but{" "}
                    <span className="font-medium text-foreground">
                      creativity, design, vision and decisions
                    </span>{" "}
                    stay human.
                  </>
                )}
              </p>
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 justify-start">
                <Button variant="hero" size="lg" className="h-14 px-8" asChild>
                  <Link
                    href={routes.contacts}
                    onClick={() =>
                      trackEvent("cta_click", {
                        cta_text: isItalian
                          ? "Lavoriamo insieme"
                          : "Let's work together",
                        cta_section: "about_hero",
                      })
                    }
                  >
                    {isItalian ? "Lavoriamo insieme" : "Let's work together"}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  className="rotating-border h-14 px-6"
                  asChild
                >
                  <Link
                    href={routes.engineering}
                    onClick={() =>
                      trackEvent("cta_click", {
                        cta_text: isItalian
                          ? "Profilo tecnico"
                          : "Technical profile",
                        cta_section: "about_hero",
                      })
                    }
                  >
                    {isItalian ? "Profilo tecnico" : "Technical profile"}
                    <Code2 className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </motion.div>

            {/* Portrait with electric border */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative mx-auto w-full max-w-[440px] sm:mt-10 xl:mt-0"
            >
              {!photoError ? (
                <div className="relative group isolate mx-auto max-w-[220px] sm:max-w-[250px]">
                  <div className="pointer-events-none absolute inset-0 z-10 rounded-3xl border border-[hsl(var(--primary)/0.72)] shadow-[0_0_0_1px_hsl(var(--primary)/0.75),0_0_14px_hsl(var(--primary)/0.75),0_0_30px_hsl(var(--primary)/0.52),0_0_52px_hsl(var(--primary)/0.28),inset_0_0_22px_hsl(var(--primary)/0.36)]" />
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    className="absolute inset-0 z-20 w-full h-full pointer-events-none"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <filter
                        id="electric-border-chaos"
                        colorInterpolationFilters="sRGB"
                        x="-22%"
                        y="-22%"
                        width="144%"
                        height="144%"
                      >
                        <feTurbulence
                          type="turbulence"
                          baseFrequency="0.018"
                          numOctaves="2"
                          result="noise1"
                          seed="1"
                        />
                        <feOffset
                          in="noise1"
                          dx="0"
                          dy="0"
                          result="offsetNoise1"
                        >
                          <animate
                            attributeName="dy"
                            values="520;0"
                            dur="6s"
                            repeatCount="indefinite"
                            calcMode="linear"
                          />
                        </feOffset>
                        <feTurbulence
                          type="turbulence"
                          baseFrequency="0.018"
                          numOctaves="2"
                          result="noise2"
                          seed="1"
                        />
                        <feOffset
                          in="noise2"
                          dx="0"
                          dy="0"
                          result="offsetNoise2"
                        >
                          <animate
                            attributeName="dy"
                            values="0;-520"
                            dur="6s"
                            repeatCount="indefinite"
                            calcMode="linear"
                          />
                        </feOffset>
                        <feTurbulence
                          type="turbulence"
                          baseFrequency="0.018"
                          numOctaves="2"
                          result="noise3"
                          seed="2"
                        />
                        <feOffset
                          in="noise3"
                          dx="0"
                          dy="0"
                          result="offsetNoise3"
                        >
                          <animate
                            attributeName="dx"
                            values="440;0"
                            dur="6s"
                            repeatCount="indefinite"
                            calcMode="linear"
                          />
                        </feOffset>
                        <feTurbulence
                          type="turbulence"
                          baseFrequency="0.018"
                          numOctaves="2"
                          result="noise4"
                          seed="2"
                        />
                        <feOffset
                          in="noise4"
                          dx="0"
                          dy="0"
                          result="offsetNoise4"
                        >
                          <animate
                            attributeName="dx"
                            values="0;-440"
                            dur="6s"
                            repeatCount="indefinite"
                            calcMode="linear"
                          />
                        </feOffset>
                        <feComposite
                          in="offsetNoise1"
                          in2="offsetNoise2"
                          result="part1"
                        />
                        <feComposite
                          in="offsetNoise3"
                          in2="offsetNoise4"
                          result="part2"
                        />
                        <feBlend
                          in="part1"
                          in2="part2"
                          mode="color-dodge"
                          result="combinedNoise"
                        />
                        <feDisplacementMap
                          in="SourceGraphic"
                          in2="combinedNoise"
                          scale="12"
                          xChannelSelector="R"
                          yChannelSelector="B"
                        />
                      </filter>
                      <filter
                        id="electric-border-soft-glow"
                        x="-50%"
                        y="-50%"
                        width="200%"
                        height="200%"
                      >
                        <feGaussianBlur stdDeviation="3.4" />
                      </filter>
                    </defs>
                    <rect
                      x="1%"
                      y="1%"
                      width="98%"
                      height="98%"
                      rx="24"
                      ry="24"
                      fill="none"
                      stroke="hsl(var(--primary) / 0.98)"
                      strokeWidth="1.9"
                      style={{ filter: "url(#electric-border-soft-glow)" }}
                    />
                    <rect
                      x="1%"
                      y="1%"
                      width="98%"
                      height="98%"
                      rx="24"
                      ry="24"
                      fill="none"
                      stroke="hsl(var(--primary) / 0.96)"
                      strokeWidth="1.35"
                      style={{ filter: "url(#electric-border-chaos)" }}
                    />
                  </svg>

                  <div className="relative z-10 rounded-3xl p-[1.5px] overflow-hidden">
                    <motion.div
                      aria-hidden
                      className="pointer-events-none absolute -inset-3 z-0 rounded-[1.95rem] bg-primary/20 blur-2xl"
                      animate={{ opacity: [0.16, 0.38, 0.22, 0.42, 0.16] }}
                      transition={{
                        duration: 2.3,
                        ease: "easeInOut",
                        repeat: Infinity,
                      }}
                    />
                    <div className="relative z-10 rounded-[calc(1.5rem-1.5px)] border border-[hsl(var(--primary)/0.28)] bg-surface/70 p-2 backdrop-blur-sm overflow-hidden transition-all duration-500 group-hover:border-primary/60 group-hover:shadow-[0_18px_48px_hsl(var(--primary)/0.22)]">
                      <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(circle_at_18%_20%,hsl(var(--primary)/0.25),transparent_45%)]" />
                      <picture>
                        <source
                          media="(max-width: 767px)"
                          srcSet="/about/dario-portrait-9x16.webp"
                          type="image/webp"
                          sizes="(max-width: 767px) 88vw, 420px"
                        />
                        <source
                          media="(min-width: 768px)"
                          srcSet="/about/dario-portrait-9x16.webp"
                          type="image/webp"
                          sizes="(max-width: 1279px) 52vw, 760px"
                        />
                        <source
                          media="(max-width: 767px)"
                          srcSet="/about/dario-portrait-9x16-original.png"
                          sizes="(max-width: 767px) 88vw, 420px"
                        />
                        <source
                          media="(min-width: 768px)"
                          srcSet="/about/dario-portrait-9x16-original.png"
                          sizes="(max-width: 1279px) 52vw, 760px"
                        />
                        <img
                          src="/about/dario-portrait-9x16.webp"
                          srcSet="/about/dario-portrait-9x16.webp"
                          sizes="(max-width: 767px) 88vw, (max-width: 1279px) 52vw, 760px"
                          alt={
                            isItalian
                              ? "Ritratto di Dario Marco Bellini"
                              : "Portrait of Dario Marco Bellini"
                          }
                          className="w-full h-auto rounded-2xl object-cover aspect-[9/16] transition-transform duration-700 ease-out group-hover:scale-[1.025] group-hover:rotate-[0.35deg]"
                          loading="eager"
                          decoding="async"
                          width={1684}
                          height={2528}
                          onError={() => setPhotoError(true)}
                        />
                      </picture>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="aspect-square max-w-md mx-auto relative">
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-primary/20 to-accent/10 rotate-6" />
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-tr from-surface to-surface-hover -rotate-3" />
                  <div className="absolute inset-4 rounded-2xl bg-surface border border-[hsl(var(--border))] flex items-center justify-center">
                    <div className="text-center">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary to-primary-glow mx-auto mb-4 flex items-center justify-center">
                        <span className="font-display text-4xl font-bold text-primary-foreground">
                          D
                        </span>
                      </div>
                      <p className="font-mono text-sm text-muted-foreground">
                        Creative Developer
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6 flex w-full flex-col gap-3 sm:flex-row">
                {/* "Collegati con me": bottone bianco pieno, spicca sul vicino */}
                <a
                  href="https://linkedin.com/in/dariomarcobellini"
                  onClick={() =>
                    trackEvent("cta_click", {
                      cta_text: isItalian ? "Collegati con me" : "Connect with me",
                      cta_section: "about_hero",
                    })
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-14 flex-1 items-center justify-center gap-2 rounded-xl border border-white bg-white px-5 text-[#05070A] transition-colors hover:bg-white/90"
                >
                  <Linkedin className="h-5 w-5" />
                  <span className="whitespace-nowrap text-base font-semibold">
                    {isItalian ? "Collegati con me" : "Connect with me"}
                  </span>
                </a>
                <a
                  href="https://linkedin.com/company/dimensione-4-di-dario-marco-bellini"
                  onClick={() =>
                    trackEvent("cta_click", {
                      cta_text: "LinkedIn Dimensione 4",
                      cta_section: "about_hero",
                    })
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex h-14 flex-1 items-center justify-center gap-2 rounded-xl border border-[hsl(var(--border))] bg-surface/70 px-5 transition-colors hover:border-primary/50 hover:bg-surface"
                >
                  <Linkedin className="h-5 w-5 text-primary" />
                  <span className="whitespace-nowrap text-base font-medium text-foreground">
                    Dimensione 4
                  </span>
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <InBreve
        text={
          isItalian
            ? "Sono Dario Marco Bellini e dietro Dimensione 4 c'è una persona sola, non un'agenzia. Questo vuol dire che parli da subito con chi progetta, disegna e scrive il codice: meno passaggi, decisioni rapide e un unico referente dall'idea alla messa online. In questa pagina trovi come lavoro, cosa costruisco e perché tengo insieme direzione, design e sviluppo in un solo flusso. Il risultato è chiaro: codice e proprietà restano tuoi, i prezzi sono trasparenti e segui ogni passo con qualcuno che fa davvero il lavoro. Se ti convince, prenota una call e ne parliamo dal vivo."
            : "I'm Dario Marco Bellini, and behind Dimensione 4 there's one person, not an agency. That means you talk straight away with the same person who plans, designs and writes the code: fewer hand-offs, fast decisions and a single point of contact from the first idea to going live. On this page you'll see how I work, what I'm building, and why I keep direction, design and development together in one flow. The result is simple: the code and ownership stay yours, the prices are clear, and you follow every step with someone who actually does the work. If it sounds right, book a call and let's talk it through."
        }
      />

      {/* Vision - the missing dimension */}
      <section className="section-padding">
        <div className="container-wide">
          <div className="grid items-center gap-10 xl:grid-cols-2 xl:gap-16">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-mono text-label text-primary mb-4 block">
                Vision
              </span>
              <h2 className="font-display text-h2 font-bold mb-6">
                {isItalian
                  ? "La dimensione che manca"
                  : "The missing dimension"}
              </h2>
              <div className="space-y-4 text-body-lg text-muted-foreground">
                {isItalian ? (
                  <>
                    <p>
                      Tre dimensioni le ha qualsiasi cosa: altezza, larghezza,
                      profondità. La quarta è il{" "}
                      <span className="font-medium text-primary">tempo</span>.
                    </p>
                    <p>
                      È quella che mi ha sempre affascinato. La cercavano
                      l&apos;arte (il cubismo, gli spazi impossibili di Escher)
                      e la fisica (lo spazio-tempo di Einstein).
                    </p>
                    <p>
                      Nel digitale è la dimensione che quasi tutti dimenticano.
                      Un progetto parte, va online e si ferma lì.
                    </p>
                    <p className="text-foreground">
                      Io parto dall&apos;idea opposta: ogni soluzione che
                      costruisco per te deve durare e{" "}
                      <span className="font-medium text-primary">
                        continuare a evolvere
                      </span>{" "}
                      con la tua attività. È il senso del nome, ed è come lavoro.
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      Everything has three dimensions: height, width, depth. The
                      fourth is{" "}
                      <span className="font-medium text-primary">time</span>.
                    </p>
                    <p>
                      It is the one that always fascinated me. Art chased it
                      (Cubism, Escher&apos;s impossible spaces) and so did
                      physics (Einstein&apos;s spacetime).
                    </p>
                    <p>
                      In the digital world it is the dimension almost everyone
                      forgets. A project ships, goes live, and stops there.
                    </p>
                    <p className="text-foreground">
                      I start from the opposite idea: every solution I build for
                      you lasts and{" "}
                      <span className="font-medium text-primary">
                        keeps evolving
                      </span>{" "}
                      with your business. It is the meaning of the name, and it
                      is how I work.
                    </p>
                  </>
                )}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            >
              <FourthDimensionVisual isItalian={isItalian} />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How I work */}
      <section className="section-padding bg-surface/30">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12"
          >
            <h2 className="font-display text-h2 font-bold mb-4">
              {isItalian ? "Come lavoro" : "How I work"}
            </h2>
            <p className="text-body-lg text-muted-foreground max-w-xxl">
              {isItalian
                ? "L'AI è uno strumento. La differenza la fanno gusto, cura e contatto diretto."
                : "AI is a tool. Taste, care, and direct contact make the difference."}
            </p>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-2">
            {/* feature: la mia differenza */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="surface-card relative flex flex-col justify-between overflow-hidden p-8 md:p-10"
            >
              <motion.div
                aria-hidden
                className="pointer-events-none absolute -right-12 -top-12 h-52 w-52 rounded-full bg-primary/15 blur-3xl"
                animate={
                  reduce
                    ? undefined
                    : { x: [0, 24, 0], y: [0, 18, 0], opacity: [0.5, 0.85, 0.5] }
                }
                transition={
                  reduce
                    ? undefined
                    : { duration: 9, repeat: Infinity, ease: "easeInOut" }
                }
              />
              <div className="relative">
                <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10">
                  <feature.icon className="h-7 w-7 text-primary" />
                </div>
                <h3 className="mb-3 font-display text-2xl font-bold md:text-3xl">
                  {isItalian ? feature.title.it : feature.title.en}
                </h3>
                <p className="max-w-sm text-body-lg text-muted-foreground">
                  {isItalian ? feature.description.it : feature.description.en}
                </p>
              </div>
              <p className="relative mt-8 font-mono text-xs uppercase tracking-[0.2em] text-primary/70">
                {isItalian ? "la mia differenza" : "my difference"}
              </p>
            </motion.div>

            {/* gli altri principi */}
            <div className="flex flex-col gap-4">
              {restValues.map((value, index) => (
                <motion.div
                  key={value.title.it}
                  initial={{ opacity: 0, x: 24 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.45, delay: 0.1 * index }}
                  className="surface-card group flex flex-1 items-start gap-4 p-5 md:p-6"
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 transition-transform duration-300 group-hover:scale-110">
                    <value.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h3 className="mb-1 font-display text-lg font-semibold">
                      {isItalian ? value.title.it : value.title.en}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {isItalian ? value.description.it : value.description.en}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What I'm building */}
      <section className="section-padding">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10 max-w-2xl"
          >
            <span className="font-mono text-label text-primary mb-4 block">
              {isItalian ? "Cosa sto costruendo" : "What I'm building"}
            </span>
            <h2 className="font-display text-h2 font-bold mb-4 lg:whitespace-nowrap">
              {isItalian
                ? "Non mi fermo ai progetti dei clienti"
                : "I don't stop at client projects"}
            </h2>
            <p className="text-body-lg text-muted-foreground">
              {isItalian
                ? "Costruisco anche cose mie. È così che resto curioso e spingo oltre."
                : "I build my own things too. That's how I stay curious and push further."}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {buildingHighlights.map((item, index) => (
              <motion.div
                key={item.title.it}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * index }}
                className="surface-card group relative overflow-hidden p-6 md:p-8"
              >
                <div className="mb-5 h-16 w-16 rounded-2xl border border-primary/15 bg-primary/5 p-2.5">
                  {item.visual === "graph" ? <NodeGraphGlyph /> : <RadarGlyph />}
                </div>
                <h3 className="mb-2 font-display text-lg font-semibold md:text-xl">
                  {isItalian ? item.title.it : item.title.en}
                </h3>
                <p className="text-sm text-muted-foreground md:text-base">
                  {isItalian ? item.description.it : item.description.en}
                </p>
              </motion.div>
            ))}
          </div>

          <div>
            <p className="mb-4 font-mono text-xs uppercase tracking-widest text-primary/90">
              {isItalian ? "In cantiere" : "In the works"}
            </p>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {buildingIdeas.map((idea, index) => (
                <motion.div
                  key={idea.label.it}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.08 }}
                  className="group relative overflow-hidden rounded-xl border border-dashed border-primary/25 bg-surface/40 p-4 transition-colors hover:border-primary/50"
                >
                  <div className="mb-4 flex items-center justify-between">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                      <idea.icon className="h-4 w-4 text-primary" />
                    </span>
                    <span
                      className="relative flex h-2 w-2"
                      title={isItalian ? "in lavorazione" : "in progress"}
                    >
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50 motion-reduce:animate-none" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                    </span>
                  </div>
                  <p className="mb-3 text-sm font-medium text-foreground">
                    {isItalian ? idea.label.it : idea.label.en}
                  </p>
                  <div className="h-1 overflow-hidden rounded-full bg-surface-hover">
                    <motion.div
                      className="h-full rounded-full bg-primary/70"
                      initial={{ width: 0 }}
                      whileInView={{ width: `${Math.round(idea.stage * 100)}%` }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: 0.2 + index * 0.08 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Technical side - link */}
      <section className="section-padding pt-0">
        <div className="container-wide">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="surface-card p-8 md:p-10 flex flex-col gap-6 md:flex-row md:items-center md:justify-between"
          >
            <div>
              <h2 className="font-display text-h3 font-bold mb-2">
                {isItalian
                  ? "Il mio background tecnico"
                  : "My technical background"}
              </h2>
              <p className="text-body text-muted-foreground max-w-xxl">
                {isItalian
                  ? "Sviluppatore full-stack, con 5 anni in Accenture su progetti enterprise: Saipem, Nexi/PagoPA, Digital Twin."
                  : "Full-stack developer, with 5 years at Accenture on enterprise projects: Saipem, Nexi/PagoPA, Digital Twin."}
              </p>
              <p className="text-body text-muted-foreground max-w-xxl">
                {isItalian
                  ? "Stack, ruoli e timeline completa nel profilo tecnico."
                  : "Stack, roles, and full timeline in the technical profile."}
              </p>
            </div>
            <Button variant="outline" size="lg" className="rotating-border h-14 px-6 shrink-0" asChild>
              <Link
                href={routes.engineering}
                onClick={() =>
                  trackEvent("cta_click", {
                    cta_text: isItalian ? "Profilo tecnico" : "Technical profile",
                    cta_section: "about_tech_link",
                  })
                }
              >
                {isItalian ? "Profilo tecnico" : "Technical profile"}
                <Code2 className="w-4 h-4" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>

    </Layout>
  );
}
